import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { addSyncListener, getSyncStatus, performFullSync } from '../../lib/syncService';
import { useAuth } from '../../contexts/AuthContext';
import { offlineScopeKey } from '../../lib/offlineScope';

export const useOfflinePreparation = () => {
  const { user } = useAuth();
  const scope = offlineScopeKey(user);
  const [offlineStatus, setOfflineStatus] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [manualBusy, setManualBusy] = useState(false);
  const [cacheRevision, setCacheRevision] = useState(0);
  useEffect(() => {
    let alive = true;
    setOfflineStatus(null);
    const update = async () => {
      try {
        const status = await getSyncStatus();
        if (alive) setOfflineStatus(status);
      } catch {
        if (alive) setOfflineStatus(previous => ({ ...previous, last_error: 'Offline storage could not be read. Please try again.' }));
      }
    };
    update();
    const unsubscribe = addSyncListener(event => {
      if (event.includes('sync') || event === 'init') update();
      if (event === 'products_sync_complete' && alive) setCacheRevision(value => value + 1);
    });
    const online = () => { setIsOffline(false); update(); };
    const offline = () => { setIsOffline(true); update(); };
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    return () => {
      alive = false;
      unsubscribe();
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
    };
  }, [scope]);

  const handleCacheProducts = useCallback(async () => {
    if (!navigator.onLine) return;
    setManualBusy(true);
    try {
      const result = await performFullSync();
      const status = await getSyncStatus();
      setOfflineStatus(status);
      if (result.success) toast.success(`Offline data refreshed: ${status.cached_products} products and ${status.cached_frequent_products} frequent items`);
      else toast.warning(status.last_error || 'Some offline data could not be refreshed. Please retry.');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Offline refresh failed');
      try { setOfflineStatus(await getSyncStatus()); } catch { /* Status listener also reports storage errors. */ }
    } finally {
      setManualBusy(false);
    }
  }, []);

  return {
    offlineStatus, isOffline, cacheRevision, handleCacheProducts,
    offlineReady: !!offlineStatus?.is_ready,
    cachingProducts: manualBusy || !!offlineStatus?.cache_sync_in_progress,
  };
};